import { Injectable, inject, signal, PLATFORM_ID } from '@angular/core';
import { isPlatformBrowser } from '@angular/common';
import { HttpClient } from '@angular/common/http';
import { Router, NavigationEnd } from '@angular/router';
import { filter } from 'rxjs/operators';

export interface ActiveAffiliateInfo {
  affiliateId: string;
  slug: string;
  referralToken: string;
  name?: string;
}

@Injectable({
  providedIn: 'root',
})
export class AffiliateTrackingService {
  private readonly http = inject(HttpClient, { optional: true });
  private readonly router = inject(Router, { optional: true });
  private readonly platformId = inject(PLATFORM_ID);

  activeAffiliate = signal<ActiveAffiliateInfo | null>(null);

  constructor() {
    if (isPlatformBrowser(this.platformId)) {
      this.loadSavedReferral();
      this.initUrlListener();
    }
  }

  private loadSavedReferral(): void {
    const cookieVal = this.getCookie('rhc_affiliate_ref');
    const localVal = localStorage.getItem('rhc_affiliate_ref');
    const slug = cookieVal || localVal;
    const affiliateId = localStorage.getItem('rhc_affiliate_id') || this.getCookie('rhc_affiliate_id') || '';
    const name = localStorage.getItem('rhc_affiliate_name') || this.getCookie('rhc_affiliate_name') || '';

    if (slug) {
      if (affiliateId || name) {
        this.activeAffiliate.set({
          affiliateId,
          slug,
          referralToken: `ref_${slug}_saved`,
          name: name || undefined,
        });
      }
      this.validateAndSetSlug(slug, false);
    }
  }

  private initUrlListener(): void {
    if (this.router) {
      this.router.events
        .pipe(filter((event): event is NavigationEnd => event instanceof NavigationEnd))
        .subscribe(() => {
          this.checkQueryParams();
        });
    }

    // Immediate check
    setTimeout(() => this.checkQueryParams(), 100);
  }

  private checkQueryParams(): void {
    if (!isPlatformBrowser(this.platformId)) return;

    const urlParams = new URLSearchParams(window.location.search);
    const via = urlParams.get('via') || urlParams.get('ref') || urlParams.get('aff');

    if (via) {
      this.validateAndSetSlug(via, true);
    }
  }

  public validateAndSetSlug(slug: string, isNewClick = true): void {
    if (!slug) return;
    const cleanSlug = slug.trim().toLowerCase();
    if (!this.http) return;

    this.http.get<{ valid: boolean; slug?: string; affiliateId?: string; name?: string }>(
      `/api/affiliates/validate/${cleanSlug}`
    ).subscribe({
      next: (res) => {
        if (res.valid && res.affiliateId && res.slug) {
          const affiliateName = res.name || localStorage.getItem('rhc_affiliate_name') || this.getCookie('rhc_affiliate_name') || undefined;
          const info: ActiveAffiliateInfo = {
            affiliateId: res.affiliateId,
            slug: res.slug,
            referralToken: `ref_${res.slug}_${Date.now()}`,
            name: affiliateName,
          };

          this.activeAffiliate.set(info);
          this.setCookie('rhc_affiliate_ref', res.slug, 60);
          localStorage.setItem('rhc_affiliate_ref', res.slug);
          localStorage.setItem('rhc_affiliate_id', res.affiliateId);
          this.setCookie('rhc_affiliate_id', res.affiliateId, 60);

          if (res.name) {
            localStorage.setItem('rhc_affiliate_name', res.name);
            this.setCookie('rhc_affiliate_name', res.name, 60);
          }

          if (isNewClick) {
            this.trackClick(res.slug);
          }
        }
      },
      error: (err) => {
        console.warn('[AffiliateTracking] Slug validation failed:', err);
      },
    });
  }

  private trackClick(slug: string): void {
    if (!this.http) return;
    const referrer = isPlatformBrowser(this.platformId) ? document.referrer : '';
    const landingPage = isPlatformBrowser(this.platformId) ? window.location.pathname + window.location.search : '/';

    this.http.post('/api/affiliates/track-click', {
      slug,
      referrer,
      landingPage,
    }).subscribe({
      error: (err) => console.warn('[AffiliateTracking] Click logging error:', err),
    });
  }

  public getActiveAffiliateId(): string | null {
    return this.activeAffiliate()?.affiliateId || (isPlatformBrowser(this.platformId) ? (localStorage.getItem('rhc_affiliate_id') || this.getCookie('rhc_affiliate_id')) : null);
  }

  public getActiveReferralToken(): string | null {
    return this.activeAffiliate()?.referralToken || (isPlatformBrowser(this.platformId) ? (localStorage.getItem('rhc_affiliate_ref') || this.getCookie('rhc_affiliate_ref')) : null);
  }

  public getActiveSlug(): string | null {
    return this.activeAffiliate()?.slug || (isPlatformBrowser(this.platformId) ? (this.getCookie('rhc_affiliate_ref') || localStorage.getItem('rhc_affiliate_ref')) : null);
  }

  public getActiveAffiliateName(): string | null {
    return this.activeAffiliate()?.name || (isPlatformBrowser(this.platformId) ? (localStorage.getItem('rhc_affiliate_name') || this.getCookie('rhc_affiliate_name')) : null);
  }

  public getActiveReferral(): { slug: string | null; affiliateId: string | null; affiliateName: string | null } {
    const slug = this.getActiveSlug();
    const affiliateId = this.getActiveAffiliateId();
    const affiliateName = this.getActiveAffiliateName();
    return { slug, affiliateId, affiliateName };
  }

  private setCookie(name: string, value: string, days: number): void {
    if (!isPlatformBrowser(this.platformId)) return;
    const date = new Date();
    date.setTime(date.getTime() + days * 24 * 60 * 60 * 1000);
    const expires = `; expires=${date.toUTCString()}`;
    const secure = window.location.protocol === 'https:' ? '; Secure' : '';
    document.cookie = `${name}=${value || ''}${expires}; path=/; SameSite=Lax${secure}`;
  }

  private getCookie(name: string): string | null {
    if (!isPlatformBrowser(this.platformId)) return null;
    const nameEQ = `${name}=`;
    const ca = document.cookie.split(';');
    for (let i = 0; i < ca.length; i++) {
      let c = ca[i];
      while (c.charAt(0) === ' ') c = c.substring(1, c.length);
      if (c.indexOf(nameEQ) === 0) return c.substring(nameEQ.length, c.length);
    }
    return null;
  }
}
